import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, IncomingInvoice, Unternehmen } from "@kundeninfo/api-client";
import { useSofortPopup } from "@kundeninfo/ui";
import { Hinweis } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import type { DocumentFilesClient } from "../../components/Vorgangsdateien.js";
import { useJumpFocus } from "../../components/jumpFocus.js";
import { useStabileListe } from "../../components/stabileListe.js";
import { sharedAssignmentLists } from "../../components/KontierungFelder.js";
import { jumpNote } from "../customerOverview/targets.js";
import type { AkteSprung } from "../KundenAkte.js";
import type { TicketWeg } from "../suppliers/LiefervertragObjekt.js";
import { useTabList } from "../suppliers/shared.js";
import type { SichtWahl } from "../suppliers/shared.js";
import { SUPPLIER_TEXT_WEB as S } from "../suppliers/texts.js";
import { ErfassungsPanel } from "./ErfassungsPanel.js";
import type { IncomingPanelClient } from "./ErfassungsPanel.js";
import { EingangsrechnungObjekt, useIncomingActions } from "./EingangsrechnungObjekt.js";
import { INCOMING_TEXT_WEB as T } from "./texts.js";
import "./IncomingInvoices.css";

/**
 * Der Reiter „EINGANGSRECHNUNGEN“ der Akte eines Lieferanten (Spec „Akte eines Lieferanten“; seit „Öffnen statt Springen“
 * Aufgabe 3 als Liste von `EingangsrechnungObjekt`, K13): in der Ordnung des Servers (das Schlechteste oben), alle
 * klappbar, in EINEM Behälter (W-4). Aufgeklappt Eckdaten, Positionen mit Kontierung, gebundene Posten, Dateien — erst dann
 * geladen. Die Aktionen stehen im Fuß (Panelknopf Bearbeiten, „⋯“: Zahlung bestätigen, Zahlung zurücknehmen, Stornieren,
 * Ticket anlegen), ohne Sprung (eigene Akte, P9); oben „Eingangsrechnung erfassen“, gesperrt ohne Lieferantenrolle im
 * aktiven Unternehmen (A-W6). Nach jedem Schreibweg — auch einem gescheiterten — lädt die Liste neu und hält das
 * Objekt an seiner Stelle (`keepInView`); ein Fehlschlag beim Zurücknehmen steht als Hinweis über der Liste. Ein Sprung
 * (`oeffneId`) klappt die Rechnung auf, fokussiert den Klapp-Pfeil und nennt seine Quelle (`useJumpFocus`, `jumpNote`).
 */

export type IncomingListClient = IncomingPanelClient & DocumentFilesClient & Pick<ApiClient,
  "getIncomingInvoices" | "getIncomingInvoice" | "confirmIncomingPayment" | "cancelIncomingInvoice">;

export function KundeEingangsrechnungen(p: {
  customerId: string; sicht: SichtWahl; client: IncomingListClient; aktivesUnternehmen: string;
  onFehler: (e: ApiError) => void; unternehmen?: readonly Unternehmen[]; onOrdnerOeffnen?: (folderId: string) => void;
  oeffneId?: string | null; jumpSource?: AkteSprung["source"];
  /** Der Ticketweg der Akte: ohne ihn kein „Ticket anlegen“ im „⋯“. */
  ticket?: TicketWeg;
  /** Das Kürzel des aktiven Unternehmens, wenn die Firma dort keine Lieferantenrolle hat; sonst `null`/weggelassen. */
  missingRoleAt?: string | null;
}): ReactElement {
  const { client, customerId } = p;
  const sicht = useStabileListe(p.sicht);
  const [open, setOpen] = useState<string | null>(null);
  const [jumped, setJumped] = useState<string | null>(null);
  const [erfassen, setErfassen] = useState(false);
  const [lists] = useState(() => sharedAssignmentLists(client));
  const key = `${customerId}|${JSON.stringify(sicht ?? null)}`;
  const list = useTabList<IncomingInvoice>("data-incoming-id", key,
    () => client.getIncomingInvoices(customerId, sicht === undefined ? {} : { companyIds: sicht }),
    (e) => { if (e instanceof ApiError) p.onFehler(e); });
  const { items, load } = list;
  const companies = p.unternehmen ?? [];
  const aktionen = useIncomingActions({ client, lists, companies, ...(p.ticket === undefined ? {} : { ticket: p.ticket }),
    onDone: (id) => { void load(id); } });
  // Ein Wechsel der Sicht klappt zu: Die aufgeklappte Rechnung steht in der neuen nicht unbedingt.
  useEffect(() => { setOpen(null); }, [key]);
  // Der Fokus des Sprungs landet am Klapp-Pfeil: `me-objekt.focus()` leitet dorthin (W-8).
  useJumpFocus("data-incoming-id", p.oeffneId, items,
    { waitForEntry: true, open: (id) => { setOpen(id); setJumped(id); } });
  const missingRole = p.missingRoleAt ?? null;
  const roleTip = useSofortPopup<HTMLSpanElement>(missingRole === null ? "" : S.noSupplierRole(missingRole));

  return (
    <div ref={list.root} className="inc">
      <div className="inc__toolbar">
        <span ref={roleTip}>
          <KiKnopf art="primaer" deaktiviert={erfassen || aktionen.offen || missingRole !== null}
            onClick={() => setErfassen(true)}>{T.record}</KiKnopf>
        </span>
        {missingRole !== null && <p className="inc__muted">{S.noSupplierRole(missingRole)}</p>}
      </div>
      {aktionen.fehler !== null && <Hinweis art="fehler">{aktionen.fehler}</Hinweis>}
      {list.failed && <Hinweis art="fehler">{T.loadFailed}</Hinweis>}
      {items === null && !list.failed && <p className="inc__muted" role="status">{T.loading}</p>}
      {items !== null && items.length === 0 && !list.failed && <p className="inc__muted">{T.empty}</p>}
      {items !== null && items.length > 0 && (
        // W-4: alle Objekte direkte Geschwister EINES Behälters.
        <div className="inc__objekte">
          {items.map((inv) => (
            <EingangsrechnungObjekt key={inv.id} rechnung={inv} ort="akte" aufgeklappt={open === inv.id}
              onAufgeklappt={(offen) => { setOpen(offen ? inv.id : null); setJumped(null); }}
              client={client} lists={lists} companies={companies} mehrereUnternehmen={sicht !== undefined && sicht.length > 1}
              mitTicket={aktionen.mitTicket} onAktion={(a) => aktionen.start(a, inv)}
              {...(p.onOrdnerOeffnen === undefined ? {} : { onOrdnerOeffnen: p.onOrdnerOeffnen })}
              jumpNote={jumped === inv.id ? jumpNote(p.jumpSource, T.jumpFromArea) : null} />
          ))}
        </div>
      )}

      {erfassen && (
        <ErfassungsPanel customerId={customerId} companyId={p.aktivesUnternehmen} client={client} lists={lists} verlauf
          onGespeichert={() => { void load(); }} onSchliessen={() => setErfassen(false)} />
      )}
      {aktionen.panels}
    </div>
  );
}
